import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LiveSession from '../../../public/Icons/LiveSession.png';
import FormulaSheets from '../../../public/Icons/FormulaSheets.png';
import ExportPDF from '../../../public/Icons/ExportPDF.png';
import RevisionNotes from '../../../public/Icons/RevisionNotes.png';
import Syllabus from '../../../public/Icons/Syllabus.png';
import Telegram from '../../../public/Icons/Telegram.png';
import Calendar from '../../../public/Icons/Calendar.png';
import React, { useEffect, useRef, useState } from 'react';
import * as PhosphorIcons from '@phosphor-icons/react';

export default function Resources({ packId, packData }) {
  const navigate = useNavigate();
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const scrollContainerRef = useRef(null);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const isScrollable = scrollWidth > clientWidth;

    if (!isScrollable) {
      setShowLeftShadow(false);
      setShowRightShadow(false);
      return;
    }

    setShowLeftShadow(scrollLeft > 0);
    setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      handleScroll();

      container.addEventListener('scroll', handleScroll);

      const resizeObserver = new ResizeObserver(handleScroll);
      resizeObserver.observe(container);

      return () => {
        container.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();
      };
    }
  }, [packData]);

  const handleResourceClick = (resource) => {
    let url = resource.url;
    const isExternalLink = resource.isExternalLink;
    if (isExternalLink) {
      window.open(url, '_blank');
    } else {
      url = url.replace(':packId', packId);
      navigate(url);
    }
  };

  const baseResources = [
    {
      isLocked: false,
      title: 'Live Session Recording',
      icon: <img src={LiveSession} className="h-6 w-6" />,
      bgColor: 'bg-[var(--q3-danger-light)]',
      type: 'session-recording',
      isVisible: packData?.resources[0]?.url ? true : false,
    },
    {
      isLocked: false,
      title: 'Telegram Channel',
      icon: <img src={Telegram} className="h-6 w-6" />,
      bgColor: 'bg-[var(--q3-blue-light)]',
      type: 'telegram',
      isVisible: packData?.resources[1]?.url ? true : false,
    },
    {
      isLocked: false,
      title: 'Tests Schedule',
      icon: <img src={Calendar} className="h-6 w-6" />,
      bgColor: 'bg-[var(--q3-violet-light)]',
      type: 'test-schedule',
      isVisible: packData?.resources[2]?.url ? true : false,
    },
    {
      isLocked: false,
      title: 'Formula Sheets',
      icon: <img src={FormulaSheets} className="h-6 w-6" />,
      bgColor: 'bg-[var(--q3-fuchsia-light)]',
      type: 'formula-sheets',
      isVisible: packData?.resources[3]?.url ? true : false,
    },
    {
      isLocked: false,
      title: 'Revision Notes',
      icon: <img src={RevisionNotes} className="h-6 w-6" />,
      bgColor: 'bg-[var(--q3-emerald-light)]',
      type: 'revision-notes',
      isVisible: packData?.resources[4]?.url ? true : false,
    },
    {
      isLocked: false,
      title: 'Important PDFs',
      icon: <img src={ExportPDF} className="h-6 w-6" />,
      bgColor: 'bg-[var(--q3-orange-light)]',
      type: 'important-pdfs',
      isVisible: packData?.resources[5]?.url ? true : false,
    },
    {
      isLocked: false,
      title: 'JEE Main Syllabus',
      icon: <img src={Syllabus} className="h-6 w-6 " />,
      bgColor: 'bg-[var(--q3-cyan-light)] ',
      type: 'syllabus',
      isVisible: packData?.resources[6]?.url ? true : false,
    },
  ];

  // Filter resources based on visibility
  const visibleResources = packData.resources;
  if (!packData?.resources) {
    return <></>;
  } else
    return (
      <div className="border-t-1 border-b-1 md:border-none py-3 px-4 md:px-2">
        <div className="w-full">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-2xl font-bold text-[var(--q3-neutral-default)] ">Resources</h2>
          </div>
          <div className="relative">
            {showLeftShadow && (
              <div className="absolute left-0 top-0 bottom-0 w-8 z-3 pointer-events-none [background:linear-gradient(to_right,var(--q3-neutral-default_inverted),transparent)]" />
            )}

            {showRightShadow && (
              <div className="absolute right-0 top-0 bottom-0 w-8 z-3 pointer-events-none [background:linear-gradient(to_left,var(--q3-neutral-default_inverted),transparent)]" />
            )}
            <div ref={scrollContainerRef} className="flex gap-8 py-1 px-1 overflow-x-auto ">
              {visibleResources.map((resource, index) => (
                <div key={index} className="flex-shrink-0 text-center group overflow-visible">
                  <Card
                    className={`bg-[${resource.fillColor}] overflow-visible border-none shadow-none flex items-center justify-center h-13 rounded-full mx-auto w-28 transition-transform duration-300 cursor-pointer hover:scale-105`}
                    onClick={() => handleResourceClick(resource)}
                  >
                    <div className={`text-[${resource.iconColor}]`}>
                      {PhosphorIcons[resource.icon]
                        ? React.createElement(PhosphorIcons[resource.icon], {
                            size: 24,
                            color: resource.iconColor,
                          })
                        : null}
                    </div>
                  </Card>
                  <p className="mt-2 text-[var(--q3-neutral-default)] text-md font-medium text-center w-28 mx-auto break-words">
                    {resource.title}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
}
